/**
 * REST client for the public CATALOG (guest-first). The public course
 * catalog is SERVER-authoritative (not sync): browsing and opening a course
 * does NOT require an account. Progress, on the other hand, is private and goes through PowerSync.
 *
 * SINGLE SOURCE OF TYPES (plan 06, E0): the DTOs are IMPORTED from the
 * OpenAPI generated from `models/course.rs`'s `struct`s. This file declares
 * NO type of its own — a hand-written interface mimicking a Rust struct is a
 * second source of truth that diverges at the first fix
 * (`docs/DATA_ACCESS_POLICY.md` §3.3). If a DTO is missing from the generated
 * output, the cause is that its handler isn't in `apps/server/src/main.rs`'s `paths(...)`.
 */
import type { components } from "@space/client";

export type CatalogEntryDTO = components["schemas"]["CatalogEntryDTO"];
export type CourseTreeDTO = components["schemas"]["CourseTreeDTO"];

function joinUrl(base: string, path: string): string {
  return `${base.replace(/\/$/, "")}${path}`;
}

/**
 * Parses a server JSON response into its typed DTO. `Response.json()` is `any`,
 * so this is the single validated trust-boundary parse for the catalog
 * endpoints (our own server contract).
 */
async function readJson<T>(res: Response): Promise<T> {
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- trust-boundary parse of our server's typed catalog DTO
  return (await res.json()) as T;
}

/** Lists the public catalog (optionally filtered by text). */
export async function fetchCatalog(
  baseUrl: string,
  opts: { q?: string; signal?: AbortSignal } = {},
): Promise<CatalogEntryDTO[]> {
  const url = new URL(joinUrl(baseUrl, "/api/catalog"));
  if (opts.q && opts.q.trim() !== "") {
    url.searchParams.set("q", opts.q.trim());
  }
  const res = await fetch(url.toString(), { signal: opts.signal });
  if (!res.ok) {
    throw new Error(`catalog fetch failed: ${res.status}`);
  }
  return readJson<CatalogEntryDTO[]>(res);
}

/** Full tree of a public course (for offline consumption after downloading). */
export async function fetchCatalogCourse(
  baseUrl: string,
  courseId: string,
  opts: { signal?: AbortSignal } = {},
): Promise<CourseTreeDTO> {
  const res = await fetch(joinUrl(baseUrl, `/api/catalog/course/${encodeURIComponent(courseId)}`), {
    signal: opts.signal,
  });
  if (!res.ok) {
    throw new Error(`course fetch failed: ${res.status}`);
  }
  return readJson<CourseTreeDTO>(res);
}
